import {html, HTMLTemplateResult, nothing, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {keyed} from "lit/directives/keyed.js";
import {createRef, ref, Ref} from "lit/directives/ref.js";
import {get, translate} from "lit-translate";
import {container} from "tsyringe";

import {RICH_TEXT_CHANGED_EVENT, RICH_TEXT_DRAFT_RESTORED_EVENT} from "@o2d/rich-text-editor";
import type {O2dRichTextEditorElement, RichTextChangedEvent, RichTextDocument} from "@o2d/rich-text-editor";

import {appState} from "../../../app.state";
import {BackendClient} from "../../../connectivity/backend-client";
import {LockConflictError} from "../../../connectivity/lock-conflict.error";
import {KnowledgeObject} from "../../../domain/entities/knowledge-object.entity";
import {deriveLocked} from "../../../domain/helpers/derive-locked.helper";
import {
    anotherHolderHoldsLock,
    EditLockHolder,
    registerEditLockHolder,
    unregisterEditLockHolder,
} from "../../../domain/helpers/edit-lock-holders.helper";
import {acquireKnowledgeObjectEditLock} from "../../../domain/helpers/acquire-knowledge-object-edit-lock.helper";
import {hasKnowledgeDraft, KnowledgeDraftChangeNotifier} from "../../../domain/helpers/knowledge-draft-registry.helper";
import {
    clearRteDraftMeta,
    discardRteDraft,
    isRteDraftForeign,
    loadRteDraftMeta,
    saveRteDraftMeta,
} from "../../../domain/helpers/rte-draft-meta.helper";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";
import {buildAssetEmbedSrc} from "../../../helpers/asset-embed.helper";
import {renderKnowledgeFilterToggleButton} from "../../../helpers/knowledge-filter-toggle.helper";
import {RichTextDocumentHelper} from "../../../helpers/rich-text-document.helper";
import {UiHelper} from "../../../helpers/ui.helper";
import {GlobalNotificationService} from "../../../services/global-notification.service";
import {AbstractLitElement} from "../../abstract-lit.element";

// Empty ProseMirror document — the terminal fallback for the save payload when
// neither a cached edit nor a live editor snapshot is available (both branches
// are practically unreachable once the Save button is enabled, but the fallback
// keeps `payload` typed as a non-null `RichTextDocument`). Mirrors the package's
// own EMPTY_DOC (`o2d-rich-text-editor.element.ts:24`).
const EMPTY_DOC: RichTextDocument = {type: 'doc', content: []};

/** A lock request still in flight — see `KnowledgeRte._pendingLockRequest`. */
interface PendingLockRequest {
    uid: string;
    result: Promise<boolean>;
    /**
     * Set by `disconnectedCallback`: it appended the release to `result`, so
     * the caller awaiting `result` must not release a second time.
     */
    releaseChained: boolean;
}

// Side-effect import: registers <o2d-rich-text-editor> custom element via the
// @customElement decorator. The package barrel (src/index.ts) exports the element
// class whose module registration runs on import.
import "@o2d/rich-text-editor";
// The package's structural CSS (editor/table/task-list layout) used to be
// pulled here as a side-effect import — Stefan, 2026-08-30: ALL CSS now lives
// in horstplan's own stylesheets (no external stylesheet imports, no inline
// CSS). The editor rules are in backend/public/assets/css/whiteboard_rte.css,
// section 1 (moved there from ralphy.css on 2026-09-03).

/**
 * Rich-text rendering pane for a single KnowledgeObject of type `document`.
 *
 * Wraps `<o2d-rich-text-editor>` plus a small toolbar and orchestrates the
 * operator-fixed view→edit→save→view mode lifecycle. Loads the KnowledgeObject's
 * content lazily when `item` changes (guarded against out-of-order resolutions via
 * a generation counter), caches the latest `rich-text-changed` payload into a plain
 * field (no @state — avoids a re-render round-trip), persists via
 * `KnowledgeObjectRepository.saveContent`, and rebases the editor's dirty baseline
 * via `markSaved(payload)` before returning to view mode. Light DOM per ADR-007.
 *
 * Sibling of `knowledge-whiteboard.component.ts`, which is the structural blueprint.
 *
 * NOTE: T2.2 delivered lazy load + read-only view render; T3.1 added the toolbar
 * `_mode` @state + the "Bearbeiten" button that switches the editor to edit mode
 * via the declarative `.mode` binding (DD3 — the wrapper is the single source of
 * truth for view/edit). T3.2 adds the host-level `rich-text-changed` listener
 * (via the `RICH_TEXT_CHANGED_EVENT` constant, DD4) that caches the latest doc
 * into the plain field `_pendingDoc` and drives `_saveButtonEnabled` from the
 * `isDirty` flag. The Save button + the save lifecycle (`markSaved`-first order)
 * are added in T3.3.
 */
@customElement('knowledge-rte')
export class KnowledgeRte extends AbstractLitElement implements EditLockHolder {

    @property({type: Object})
    public item: KnowledgeObject | null = null;

    /**
     * Wrapper-owned view/edit mode, bound to `<o2d-rich-text-editor>.mode` as a
     * PROPERTY (the element reflects `mode`, so the binding also surfaces the
     * `mode="…"` attribute for Chrome-MCP assertions). Kept in a `@state` field
     * rather than driving the element imperatively (DD3): the wrapper stays the
     * single source of truth and view/edit cannot drift between the two.
     *
     * Edit-lock (Stefan, 2026-09-15: "den gleichen Lock-Mechanismus... wie bei
     * den Linkview-Kacheln"): `_mode` now follows the REAL backend lock on the
     * KnowledgeObject, not a purely local toggle. On load it is derived from
     * `deriveLocked(loaded.lockedBy, ownUid)` — same polarity/reasoning as
     * Linkview's `dashboard-creator.component.ts`, so re-opening an object
     * this User already holds the lock for (a previous session that never
     * saved/released) opens directly in edit mode. `_onEdit` acquires (or, on
     * conflict, offers to take over) the lock via
     * `acquireKnowledgeObjectEditLock()`; `_onSave` releases it again — the
     * User must click "Bearbeiten" once more for further edits, per Anke's
     * explicit requirement (Linkview's `dashboard-creator` and the whiteboard
     * behave the same way since 2026-09-17).
     */
    @state()
    public _mode: 'view' | 'edit' = 'view';

    /**
     * Loaded ProseMirror-JSON document, bound to `<o2d-rich-text-editor>.content`
     * as a PROPERTY (the element declares `@property({attribute:false})`, so an
     * attribute binding would silently no-op). `null` until the first load
     * resolves or when there is no persisted content.
     */
    public _richTextContent: RichTextDocument | null = null;

    /**
     * Plain field cache of the latest `rich-text-changed` payload — NO
     * decorator. Reactive tracking would re-render and bounce the doc back into
     * `<o2d-rich-text-editor>.content`, resetting the tiptap cursor on every
     * edit. Consumed by the save lifecycle (T3.3) as the source of truth for
     * what to persist. `null` until the first edit in the current session.
     */
    public _pendingDoc: RichTextDocument | null = null;

    /** Track item identity across willUpdate calls to skip redundant loads. */
    private _lastItemUid: string | null = null;

    /**
     * The entity `findOneByIdWithContent` actually returned — carries the
     * live `lockedBy`/`lockedAt` the lock flow reads and mutates (see
     * `_mode`'s own doc comment). NOT necessarily `this.item`: that is
     * whatever the caller bound in, e.g. a list-query instance without
     * content; this is always the one the content load resolved.
     */
    private _loadedKnowledgeObject: KnowledgeObject | null = null;

    /** Render-gate: flips once per item from `false` → `true` after load resolves. */
    @state()
    public _isContentReady = false;

    @state()
    public _isLoading = false;

    /**
     * Render-gate for the Save button's `disabled` attribute (rendered in T3.3).
     * Driven from the `rich-text-changed` `isDirty` flag; the write is
     * equality-guarded so the 300ms-debounced changed events do not trigger a
     * render storm.
     */
    @state()
    public _saveButtonEnabled = false;

    /**
     * True once the editor restored an unsaved localStorage draft for the current
     * object (per-object autosave). Drives the "unsaved draft" toolbar hint and
     * — together with the restored doc being dirty vs the persisted baseline —
     * keeps the Save button enabled so the draft can be persisted. Set from the
     * `rich-text-draft-restored` event; cleared on item-switch and after a save.
     */
    @state()
    public _hasUnsavedDraft = false;

    /**
     * Re-entrancy guard for the async save handler. Set `true` synchronously at
     * the top of `_onSave` (before the first await) so a rapid double-click
     * dispatches exactly one `saveContent` (Lit async-handler rule); also drives
     * the Save button's `?disabled` binding while a save is in flight.
     */
    @state()
    public _isSaving = false;

    /**
     * Re-entrancy guard for `_onCancel` (review fix, 2026-09-16), same
     * pattern as `_isSaving` — `_onCancel` awaits a confirm dialog before
     * its first state mutation, so a rapid double-click could otherwise
     * open two confirm dialogs / fire two `releaseLock` calls.
     */
    @state()
    public _isCancelling = false;

    /**
     * Re-entrancy guard for `_onEdit` (review 2026-09-19, H1): it awaits the
     * lock, a re-read and up to two dialogs before flipping `_mode`, so a
     * double-click would otherwise stack two "Neuere Version vorhanden"
     * dialogs whose contradicting answers act on the same draft. Also
     * disables the "Bearbeiten" button while it runs.
     */
    @state()
    public _isEditing = false;

    /**
     * Typed reference to the inner `<o2d-rich-text-editor>`, obtained via the
     * `ref()` directive. Used imperatively at save time to read the live
     * `getSnapshot().doc` (payload fallback) and to rebase the dirty baseline via
     * `markSaved(payload)` — neither is expressible declaratively.
     */
    public _editorRef: Ref<O2dRichTextEditorElement> = createRef<O2dRichTextEditorElement>();

    /**
     * Monotonic counter incremented on every `item` change. Each in-flight
     * `findOneByIdWithContent` call captures the value at dispatch time and
     * discards its result if the counter has moved on (stale-promise guard).
     */
    private _loadGeneration = 0;

    /**
     * The `acquireKnowledgeObjectEditLock` call in flight, from `_onEdit` or
     * the silent resume in `willUpdate` (plain field, nothing renders it).
     * Review 2026-09-19 (K2): the server locks per entity and user, not per
     * element — an unmount while this is pending runs `disconnectedCallback`
     * in 'view' mode, and the lock lands on the server right after (no TTL).
     * `_onEdit` was already covered: its `stillHere()` checks `isConnected`
     * and hands the late lock to `_releaseAbandonedLock`. The silent resume
     * only checked the generation, so ITS lock stayed behind — that is the
     * gap the chain in `disconnectedCallback` closes; for `_onEdit` it is a
     * second safeguard.
     */
    private _pendingLockRequest: PendingLockRequest | null = null;

    public async willUpdate(changedProperties: PropertyValues): Promise<void> {
        if (!changedProperties.has('item')) {
            return;
        }
        const item = this.item;
        if (!item) {
            this._richTextContent = null;
            this._pendingDoc = null;
            this._saveButtonEnabled = false;
            this._hasUnsavedDraft = false;
            this._isContentReady = false;
            this._isLoading = false;
            this._lastItemUid = null;
            this._loadedKnowledgeObject = null;
            // Reset to locked/view: the element is reused across items
            // (knowledge-view-pane), so a lingering 'edit' mode would open the
            // next document directly editable.
            this._mode = 'view';
            return;
        }
        if (item.uid === this._lastItemUid) {
            return;
        }

        // Increment BEFORE any await so that subsequent calls invalidate this one.
        this._loadGeneration += 1;
        const myGeneration = this._loadGeneration;
        this._isLoading = true;
        this._isContentReady = false;
        this._pendingDoc = null;
        this._saveButtonEnabled = false;
        this._hasUnsavedDraft = false;
        // Switching to a new object always opens it locked, until the load
        // below re-derives it from the object's actual lock state.
        this._mode = 'view';

        const repository = container.resolve(KnowledgeObjectRepository);
        try {
            const loaded = await repository.findOneByIdWithContent(item.uid);
            if (myGeneration !== this._loadGeneration) {
                // A newer load has been kicked off — discard this stale result.
                return;
            }
            // LOAD-side cast + guard (DD2/OQ1): the entity types `content.payload`
            // as the whiteboard-era `unknown[]`, but a `document` persists a
            // ProseMirror doc. A freshly-created object seeds `content.payload = []`
            // (the backend create handler uses an empty array for ALL types), which
            // is NOT a valid RichTextDocument — treat any non-`{type:'doc'}` payload
            // as absent so the editor renders an empty document rather than choking
            // on an array.
            const rawPayload = loaded?.content?.payload as unknown;
            this._richTextContent = RichTextDocumentHelper.isRichTextDocument(rawPayload) ? rawPayload : null;
            this._loadedKnowledgeObject = loaded;
            // Set BEFORE the awaits below (stale-draft dialog, lock acquire): a
            // re-bind of the SAME uid while a dialog is open (e.g. the sidebar's
            // `reload()` swapping the `item` reference) must hit the early return
            // above instead of starting a second load with a second dialog.
            this._lastItemUid = item.uid;
            // Held already (e.g. a previous session that never saved/released)
            // -> open directly in edit mode, mirroring dashboard-creator's own
            // `_locked` derivation — see `_mode`'s own doc comment.
            this._mode = deriveLocked(loaded?.lockedBy ?? null, appState.user?.uid ?? null) ? 'view' : 'edit';

            // Bug fix (Anke, 2026-09-18, BUG2: "Dok1 [RTE] bearbeiten > Text
            // eintippen > zu White1 [Whiteboard] wechseln > bearbeiten >
            // etwas ändern > zurück zu Dok1 > nicht mehr im
            // Bearbeitungsmodus, obwohl Text noch schräg ist und Dokument
            // ungespeichert ist"): switching to a KnowledgeObject of the
            // OTHER content type unmounts THIS wrapper entirely
            // (`knowledge-view-pane.component.ts` swaps between
            // `<knowledge-rte>`/`<knowledge-whiteboard>` by `item.type`, only
            // ONE of which is ever mounted) — firing `disconnectedCallback`,
            // which releases the lock but deliberately keeps the local draft
            // (discarding it there would silently lose unsaved work, and
            // `disconnectedCallback` cannot await a confirm dialog to ask
            // first). Re-mounting on return therefore reloads fresh with a
            // FREE lock, stranding the reopened document in plain view mode
            // while the sidebar still shows it unsaved. Mirrors the identical
            // fix in `knowledge-whiteboard.component.ts` (see its own
            // `willUpdate` for the full reasoning, including why a lock held
            // by somebody ELSE is deliberately left alone here): a free lock
            // with a leftover draft resumes edit mode silently via the same
            // `acquireKnowledgeObjectEditLock` call "Bearbeiten" itself makes.
            //
            // Review 2026-09-19 (F12/F13): the package's draft carries neither
            // an owner nor a baseline, so the resume is gated on horstplan's
            // side-car meta (`rte-draft-meta.helper.ts`): own user + matching
            // baseline resumes silently, a diverging baseline asks first, a
            // missing meta (owner unknown) never resumes — the Sidebar star
            // stays and the user clicks "Bearbeiten" explicitly. A FOREIGN
            // draft is discarded before the editor can mount and restore it.
            const userUid = appState.user?.uid ?? '';
            const foreignDiscarded = (this._mode === 'edit' || loaded?.lockedBy === null)
                && this._discardForeignDraft(item.uid, userUid);
            if (!foreignDiscarded && this._mode === 'view' && loaded !== null && loaded.lockedBy === null
                && hasKnowledgeDraft(item.uid, userUid)) {
                const meta = loadRteDraftMeta(item.uid);
                let resume = userUid !== '' && meta !== undefined && meta.userUid === userUid;
                if (resume) {
                    // Generation only: the answer is bound to `item.uid`, so it
                    // still applies when the element was unmounted meanwhile —
                    // the dialog lives on `document.body` and outlives it; only
                    // the lock below is off limits then (review 2026-09-19, L2).
                    const outcome = await this._resolveStaleDraft(
                        item.uid,
                        userUid,
                        loaded.updatedAt?.toISO() ?? '',
                        () => myGeneration === this._loadGeneration,
                    );
                    if (outcome === 'stale') {
                        return;
                    }
                    resume = outcome === 'keep';
                }
                if (resume) {
                    if (this.isConnected === false) {
                        // Unmounted during the load or the dialog: a lock taken
                        // now has nobody to give it back (the K2 chain only
                        // covers a request pending AT the unmount).
                        return;
                    }
                    const request = this._requestEditLock(loaded);
                    const resumed = await request.result;
                    if (request.releaseChained) {
                        // Unmounted meanwhile: `disconnectedCallback` gives the
                        // lock back (K2), the mode stays 'view'.
                        return;
                    }
                    if (myGeneration !== this._loadGeneration) {
                        if (resumed) {
                            this._releaseAbandonedLock(item.uid);
                        }
                        return;
                    }
                    if (resumed) {
                        this._mode = 'edit';
                    }
                }
            }

            this._isContentReady = true;
        } catch (error) {
            if (myGeneration !== this._loadGeneration) {
                return;
            }
            console.error(`Failed to load rich-text content for «${item.uid}».`, error);
            this._richTextContent = null;
            this._loadedKnowledgeObject = null;
            this._isContentReady = true;
            this._lastItemUid = item.uid;
        } finally {
            if (myGeneration === this._loadGeneration) {
                this._isLoading = false;
            }
        }
    }

    public connectedCallback(): void {
        super.connectedCallback();
        registerEditLockHolder(this);
        // Host-level delegation via the exported event-name constant (DD4 — no
        // magic string). `rich-text-changed` bubbles (fireEvent sets
        // bubbles:true) and the editor renders into this Light-DOM host, so the
        // single host listener catches every emit regardless of when the inner
        // element mounts/re-mounts.
        this.addEventListener(RICH_TEXT_CHANGED_EVENT, this._onChanged);
        // Per-object draft autosave: the editor restores an unsaved localStorage
        // draft when it (re-)mounts for an object that has one — e.g. after
        // switching to another object and back — and signals it via this event
        // (NOT `rich-text-changed`, which the draft-overlay deliberately does not
        // emit). Catch it to surface the unsaved state.
        this.addEventListener(RICH_TEXT_DRAFT_RESTORED_EVENT, this._onDraftRestored);
    }

    /** See `edit-lock-holders.helper.ts`: lets a replaced sibling ask whether this element edits `uid`. */
    public holdsEditLockFor(uid: string): boolean {
        return this.item?.uid === uid && this.isConnected !== false && this._mode === 'edit';
    }

    public disconnectedCallback(): void {
        unregisterEditLockHolder(this);
        this.removeEventListener(RICH_TEXT_CHANGED_EVENT, this._onChanged);
        this.removeEventListener(RICH_TEXT_DRAFT_RESTORED_EVENT, this._onDraftRestored);
        // Review fix (2026-09-16): a best-effort release for the "navigated
        // away while still in edit mode" gap — neither `_onSave` nor
        // `_onCancel` runs when the host simply unmounts this element (e.g.
        // the user switches to a different item without saving/cancelling).
        // Fire-and-forget: `disconnectedCallback` cannot be async/awaited,
        // and this is a best effort only — it will not fire on a hard tab
        // close/crash either, same limitation the existing Linkview lock has.
        if (this._mode === 'edit' && this.item) {
            this._releaseLock(this.item.uid);
        }
        // A lock request still pending lands on the server AFTER this ran in
        // 'view' mode (K2: "Bearbeiten" or a draft resume, then an immediate
        // switch to the other content type replaces this element). The caller
        // awaiting it sees `releaseChained` and leaves the release to this
        // chain — one release per acquired lock. `uid` comes from the request,
        // not from `this.item` at settle time.
        const pending = this._pendingLockRequest;
        if (pending) {
            pending.releaseChained = true;
            pending.result.then(
                (acquired) => {
                    if (acquired) {
                        this._releaseLock(pending.uid);
                    }
                },
                () => { /* nothing was acquired; the awaiting caller reports the failure */ },
            );
        }
        super.disconnectedCallback();
    }

    /**
     * Starts the lock request and records it for `disconnectedCallback`.
     * Cleared once settled — unless a newer request replaced it meanwhile.
     */
    private _requestEditLock(entity: KnowledgeObject): PendingLockRequest {
        const request: PendingLockRequest = {
            uid: entity.uid,
            result: acquireKnowledgeObjectEditLock(entity),
            releaseChained: false,
        };
        this._pendingLockRequest = request;
        const clear = (): void => {
            if (this._pendingLockRequest === request) {
                this._pendingLockRequest = null;
            }
        };
        // Not `.finally`: that would mint a second, unhandled rejection of the
        // same error next to the one the awaiting caller already gets.
        request.result.then(clear, clear);
        return request;
    }

    /**
     * The one place a lock is given back outside Save/Cancel. Not when another
     * connected element already edits the same object (J1, review 2026-09-19):
     * the server knows only the user as holder, so this release would delete
     * the lock that element relies on. Same-user two-TAB case stays open
     * (per-page registry) — fix would be a session id stored with the lock in
     * the backend. Fire-and-forget: `disconnectedCallback` cannot await, and
     * a failed release just means the next "Bearbeiten" offers a take-over.
     */
    private _releaseLock(uid: string): void {
        if (anotherHolderHoldsLock(uid, this)) {
            return;
        }
        container.resolve(KnowledgeObjectRepository).releaseLock(uid).catch((releaseError: unknown) => {
            console.error(`Failed to release the edit lock for «${uid}».`, releaseError);
        });
    }

    /**
     * Handles the inner `<o2d-rich-text-editor>` `rich-text-changed` CustomEvent.
     * Caches the payload by reference into the plain field `_pendingDoc` (no
     * defensive copy — the detail is readonly), and writes `_saveButtonEnabled`
     * ONLY when the boolean actually flips so the 300ms-debounced changed events
     * do not trigger a render storm.
     */
    public _onChanged = (event: RichTextChangedEvent): void => {
        this._pendingDoc = event.detail.doc;
        // A live edit supersedes any restored-draft hint (the doc is now dirty in
        // its own right); keep the hint only while the dirty state persists.
        this._hasUnsavedDraft = event.detail.isDirty && this._hasUnsavedDraft;
        if (this._saveButtonEnabled !== event.detail.isDirty) {
            this._saveButtonEnabled = event.detail.isDirty;
        }
        const uid = this.item?.uid;
        const userUid = appState.user?.uid ?? '';
        if (uid && event.detail.isDirty && userUid !== '') {
            // Owner + baseline of the package's draft (F12/F13) — the package
            // stores neither; see `rte-draft-meta.helper.ts`. Written once per
            // draft: an existing meta of the SAME user keeps its baseline, or
            // the first keystroke after "Bearbeiten" would re-baseline a
            // restored, possibly stale draft to the current server state and
            // silently disarm the stale-draft check (review 2026-09-19, G2).
            // Never written for an unknown user — an entry with `userUid: ''`
            // could not be restored by anybody later (G3).
            const meta = loadRteDraftMeta(uid);
            if (meta === undefined || meta.userUid !== userUid) {
                saveRteDraftMeta(uid, {
                    userUid,
                    baselineUpdatedAt: this._loadedKnowledgeObject?.updatedAt?.toISO() ?? '',
                });
            }
        }
        // The package autosaves its own `DraftStore` entry on every edit —
        // this pings the sidebar list so its unsaved-indicator updates live.
        // DEPENDENCY: "live from the first change" only holds because
        // `@o2d/rich-text-editor` writes that draft SYNCHRONOUSLY on the first
        // dirty edit (`#onEditorUpdate` → `saveNow`, sibling repo
        // o2d-frontend-packages) — its debounced `save` would land after this
        // notify and the sidebar would read "no draft". That package change
        // must be committed/published before horstplan ships.
        if (uid) {
            container.resolve(KnowledgeDraftChangeNotifier).notify(uid);
        }
    };

    /**
     * Removes a draft that belongs to ANOTHER user (same browser, e.g. after
     * a session expiry that never cleared localStorage) BEFORE the editor can
     * mount in edit mode — the package would otherwise restore it silently
     * under this user's name.
     *
     * ACCEPTED LOSS: the package's draft key (`o2d.rte-draft.<uid>`) is fixed
     * and not user-scoped, so the real owner's unsaved draft is destroyed
     * when somebody else opens the document in the same browser without a
     * logout in between (review 2026-09-19, G4). The whiteboard avoids this
     * with a user-scoped key; here only the package could.
     *
     * Does nothing while the current user is unknown (hydration, expired
     * session with this component still mounted): "unknown" must never be
     * read as "foreign" when the action is destructive (G3).
     * Returns whether a foreign draft was discarded.
     */
    private _discardForeignDraft(uid: string, userUid: string): boolean {
        if (userUid === '' || !isRteDraftForeign(uid, userUid)) {
            return false;
        }
        discardRteDraft(uid);
        container.resolve(KnowledgeDraftChangeNotifier).notify(uid);
        return true;
    }

    /**
     * Baseline check for the user's OWN draft: when the meta's baseline no
     * longer matches the server's `updatedAt`, somebody saved since — ask
     * before the draft is applied over their version. Shared by the silent
     * resume (`willUpdate`) and the explicit "Bearbeiten" (`_onEdit`).
     *
     * A draft WITHOUT meta (owner unknown) is not checked: there is no
     * baseline to compare, and the package restores it on mount either way.
     *
     * - `'keep'`: nothing diverges, or the user chose to apply it (the meta
     *   is re-baselined so the user is not asked again for the same draft);
     * - `'discarded'`: the user chose to drop it (draft + meta removed);
     * - `'stale'`: the item was swapped while the dialog was open — nothing
     *   is touched.
     */
    private async _resolveStaleDraft(
        uid: string,
        userUid: string,
        currentUpdatedAt: string,
        isStillCurrent: () => boolean,
    ): Promise<'keep' | 'discarded' | 'stale'> {
        const meta = loadRteDraftMeta(uid);
        if (userUid === '' || meta === undefined || meta.userUid !== userUid
            || meta.baselineUpdatedAt === currentUpdatedAt || !hasKnowledgeDraft(uid, userUid)) {
            return 'keep';
        }
        const apply = await this._confirmStaleDraft();
        if (!isStillCurrent()) {
            return 'stale';
        }
        if (apply) {
            saveRteDraftMeta(uid, {userUid, baselineUpdatedAt: currentUpdatedAt});
            return 'keep';
        }
        discardRteDraft(uid);
        container.resolve(KnowledgeDraftChangeNotifier).notify(uid);
        return 'discarded';
    }

    /**
     * Re-reads the object so `updatedAt` (the draft baseline) is current, not
     * the value of its first load: `saveContent` moves the server's
     * `updatedAt` and the resulting `reload()` detaches the cached entity, so
     * `_loadedKnowledgeObject` would otherwise keep the old one and the next
     * draft would look "saved by somebody else" (review 2026-09-19, G1;
     * `knowledge-whiteboard.component.ts` does the same). `adoptContent`
     * also takes over changed server content — only valid while the editor
     * is still in view mode, since it re-binds `.content`. Best effort: a
     * failed read keeps what is there.
     */
    private async _reloadLoadedEntity(uid: string, generation: number, adoptContent: boolean): Promise<void> {
        let fresh: KnowledgeObject | null = null;
        try {
            fresh = await container.resolve(KnowledgeObjectRepository).findOneByIdWithContent(uid) ?? null;
        } catch (error) {
            console.error(`Failed to refresh rich-text document «${uid}».`, error);
        }
        if (fresh === null || generation !== this._loadGeneration) {
            return;
        }
        this._loadedKnowledgeObject = fresh;
        if (adoptContent) {
            const rawPayload = fresh.content?.payload as unknown;
            const freshContent = RichTextDocumentHelper.isRichTextDocument(rawPayload) ? rawPayload : null;
            if (JSON.stringify(freshContent) !== JSON.stringify(this._richTextContent)) {
                this._richTextContent = freshContent;
            }
        }
    }

    private _confirmStaleDraft(): Promise<boolean> {
        return UiHelper.confirm({
            title: 'Neuere Version vorhanden',
            content: html`<p>Es gibt einen ungespeicherten lokalen Entwurf für dieses
                Dokument, aber es wurde inzwischen von einer anderen Person
                gespeichert. Möchten Sie Ihren alten Entwurf trotzdem anwenden
                (überschreibt die neuere Version) oder verwerfen und mit der
                aktuellen Version weiterarbeiten?</p>`,
            confirmLabel: 'Entwurf anwenden',
            cancelLabel: 'Verwerfen',
        });
    }

    /**
     * A package draft WITHOUT meta has no provable owner: the package restores
     * it on mount either way, so the explicit "Bearbeiten" asks first
     * (review 2026-09-19, H2). Typical source: logout clears `localStorage`
     * while the editor is still mounted, and the package's unmount flush
     * (`saveNow`) then re-writes the draft AFTER the clear — without meta,
     * because that is only written 300 ms after the first edit — so the next
     * user would otherwise get the previous user's text applied silently and
     * could save it under their own name. Same outcomes as
     * `_resolveStaleDraft`; "apply" adopts the draft (meta for the current
     * user + `updatedAt`), so the question is not asked again.
     *
     * Not asked while the current user is unknown (G3: nothing destructive),
     * nor when a meta exists (`_resolveStaleDraft` owns that case).
     */
    private async _resolveUnknownOriginDraft(
        uid: string,
        userUid: string,
        currentUpdatedAt: string,
        isStillCurrent: () => boolean,
    ): Promise<'keep' | 'discarded' | 'stale'> {
        if (userUid === '' || loadRteDraftMeta(uid) !== undefined || !hasKnowledgeDraft(uid, userUid)) {
            return 'keep';
        }
        const apply = await this._confirmUnknownOriginDraft();
        if (!isStillCurrent()) {
            return 'stale';
        }
        if (apply) {
            saveRteDraftMeta(uid, {userUid, baselineUpdatedAt: currentUpdatedAt});
            return 'keep';
        }
        discardRteDraft(uid);
        container.resolve(KnowledgeDraftChangeNotifier).notify(uid);
        return 'discarded';
    }

    private _confirmUnknownOriginDraft(): Promise<boolean> {
        return UiHelper.confirm({
            title: 'Entwurf unbekannter Herkunft',
            content: html`<p>Es gibt einen ungespeicherten lokalen Entwurf für dieses
                Dokument, dessen Herkunft nicht bestätigt werden kann (er könnte von
                einer anderen Sitzung stammen). Möchten Sie ihn anwenden oder
                verwerfen und mit der gespeicherten Version weiterarbeiten?</p>`,
            confirmLabel: 'Entwurf anwenden',
            cancelLabel: 'Verwerfen',
        });
    }

    /**
     * A lock held by an `_onEdit` that finished after the item was swapped
     * away — or after this element was unmounted during the re-read or a
     * dialog (`disconnectedCallback` released nothing: the mode was still
     * 'view', and the lock request itself had already settled, so nothing was
     * chained to it either) — must not stay behind (no TTL); same rule as
     * `_onCancel`'s release. Skipped when the SAME object is current again on
     * a connected element: its own (re)load derives the mode from the server,
     * so releasing would contradict it. A disconnected element never reloads,
     * so it releases.
     */
    private _releaseAbandonedLock(uid: string): void {
        if (this.isConnected !== false && this.item?.uid === uid) {
            return;
        }
        this._releaseLock(uid);
    }

    /**
     * The editor restored an unsaved draft (e.g. after switching away and back).
     * The restored doc is dirty vs the persisted baseline, so enable Save and
     * surface the "unsaved draft" hint. The draft-overlay does NOT emit
     * `rich-text-changed`, so this is the only signal for it.
     */
    public _onDraftRestored = (): void => {
        this._hasUnsavedDraft = true;
        this._saveButtonEnabled = true;
    };

    /**
     * Switches the wrapper into edit mode — but only once the REAL backend
     * lock is actually held (Stefan, 2026-09-15). Order, and `_mode` only
     * flips at the very end:
     *
     * 1. lock: `acquireKnowledgeObjectEditLock()` (acquire, or offer the
     *    take-over — shared with `knowledge-whiteboard.component.ts`, see that
     *    helper's own doc comment);
     * 2. re-read the object with `adoptContent` so `updatedAt` and the bound
     *    content are current (G1);
     * 3. discard a draft of ANOTHER user before the editor can restore it;
     * 4. draft provenance, each with its own dialog: a diverging baseline
     *    ("Neuere Version vorhanden") or no meta at all ("Entwurf unbekannter
     *    Herkunft") — the package would otherwise restore the draft silently
     *    on mount.
     *
     * Deliberately NOT the whiteboard's order (there `_mode` flips right after
     * the lock and `_restoreDraftIfEditing` runs afterwards): the RTE's draft
     * is restored by the package when the editor mounts in edit mode, so every
     * question must be answered BEFORE the flip. The `.mode` binding then
     * propagates the change to the inner editor reactively.
     *
     * Every await can outlive the item (swap) or the element (unmount):
     * either way the lock just acquired is given back and `_mode` stays put.
     * `_isEditing` makes a second click a no-op while this runs (H1).
     */
    public _onEdit = async (): Promise<void> => {
        const entity = this._loadedKnowledgeObject;
        if (!entity || this._isLoading || this._isSaving || this._isCancelling || this._isEditing) {
            return;
        }
        this._isEditing = true;
        try {
            // Captured BEFORE the await: an item swap during the lock request or
            // a take-over dialog must not flip the NEW item into edit mode.
            const editGeneration = this._loadGeneration;
            const stillHere = (): boolean => editGeneration === this._loadGeneration && this.isConnected !== false;

            const request = this._requestEditLock(entity);
            const acquired = await request.result;
            if (!acquired) {
                return;
            }
            if (request.releaseChained) {
                // Unmounted while the request was pending: `disconnectedCallback`
                // chained the release onto it (K2) — not a second one here.
                return;
            }
            if (!stillHere()) {
                this._releaseAbandonedLock(entity.uid);
                return;
            }
            // Read after the lock is held, so nobody can save between this read
            // and the stale-draft comparison below (G1).
            await this._reloadLoadedEntity(entity.uid, editGeneration, true);
            if (!stillHere()) {
                this._releaseAbandonedLock(entity.uid);
                return;
            }
            // Before the editor mounts in edit mode (it would restore it silently).
            const userUid = appState.user?.uid ?? '';
            if (!this._discardForeignDraft(entity.uid, userUid)) {
                const updatedAt = this._loadedKnowledgeObject?.updatedAt?.toISO() ?? '';
                let outcome = await this._resolveStaleDraft(entity.uid, userUid, updatedAt, stillHere);
                if (outcome === 'keep') {
                    outcome = await this._resolveUnknownOriginDraft(entity.uid, userUid, updatedAt, stillHere);
                }
                if (outcome === 'stale') {
                    this._releaseAbandonedLock(entity.uid);
                    return;
                }
            }
            this._mode = 'edit';
        } finally {
            this._isEditing = false;
        }
    };

    /**
     * Persists the current document and returns the wrapper to view mode.
     *
     * Payload precedence: the cached `_pendingDoc` (the latest debounced
     * `rich-text-changed` doc) when present, else the editor's LIVE
     * `getSnapshot().doc` — an empty document is `{type:'doc',content:[]}`, NEVER
     * the whiteboard's `?? []` array default (DD2). The `readonly unknown[]` cast
     * is required: `saveContent`/the command payload are typed as arrays but a
     * `RichTextDocument` is a single object (the backend stores it generically).
     *
     * On success the order is operator-fixed: `markSaved(payload)` FIRST (rebase
     * the dirty baseline so any edit made during the in-flight save stays
     * surfaced as dirty against the persisted doc), THEN `mode='view'`, THEN
     * clear the cache + disable the button. On rejection: notify and stay in edit
     * mode with the cache + enabled button preserved so the user can retry.
     * Wrapped in try/catch with an `_isSaving` re-entrancy guard (Lit async rule).
     */
    public _onSave = async (): Promise<void> => {
        const item = this.item;
        if (!item || this._isSaving || this._isLoading) {
            return;
        }
        this._isSaving = true;
        const saveGeneration = this._loadGeneration;
        const editor = this._editorRef.value;
        // Payload precedence: the editor's LIVE `getSnapshot().doc` is the source
        // of truth — it reads `editor.getJSON()` directly and so captures
        // sub-300ms keystrokes the debounced `_pendingDoc` (rich-text-changed) may
        // not yet reflect (o2d-rich-text-editor.element.ts:151-164). `_pendingDoc`
        // is only a fallback for when the editor ref is not yet populated. NOTE:
        // this deliberately REVERSES the PRD's stated `_pendingDoc ?? getSnapshot()`
        // order — that order is internally inconsistent with the package's
        // getSnapshot design (hub decision, Fable-5 review).
        const payload = editor?.getSnapshot().doc ?? this._pendingDoc ?? EMPTY_DOC;
        try {
            // SAVE-side cast (DD2/OQ1): the command payload is typed
            // `readonly unknown[]` (whiteboard-era array shape) but a document's
            // payload is a single `RichTextDocument` object — the backend stores
            // it generically, so the cast bridges the type boundary.
            const repository = container.resolve(KnowledgeObjectRepository);
            await repository.saveContent(item.uid, payload as unknown as readonly unknown[]);
            // ORDER MATTERS: rebase baseline FIRST (markSaved is mode-independent),
            // then refresh the bound doc so the view/edit surface shows the SAVED
            // document (not the stale pre-edit load), then clear cache + button.
            editor?.markSaved(payload);
            this._richTextContent = payload;
            this._pendingDoc = null;
            this._saveButtonEnabled = false;
            this._hasUnsavedDraft = false;
            clearRteDraftMeta(item.uid);
            container.resolve(KnowledgeDraftChangeNotifier).notify(item.uid);
            // Release the lock and return to LOCKED view mode (Anke's explicit
            // requirement — see `_mode`'s own doc comment; Linkview's
            // dashboard-creator and the whiteboard do the same). A failed release is
            // logged but not surfaced: the content is already safely saved,
            // and a stray un-released lock just means the User re-enters via
            // "Bearbeiten" once more — self-recovering, not worth a toast.
            // ORDER (review fix, 2026-09-16): await the release BEFORE
            // flipping `_mode` — flipping first made this client's own UI
            // show "free" while the server lock could still be held (a
            // failed/slow release), which could surface a misleading
            // take-over dialog to a second user racing in during that gap.
            await repository.releaseLock(item.uid).catch((releaseError: unknown) => {
                console.error(`Failed to release the edit lock for «${item.uid}» after saving.`, releaseError);
            });
            this._mode = 'view';
            if (saveGeneration === this._loadGeneration) {
                await this._reloadLoadedEntity(item.uid, saveGeneration, false);
            }
        } catch (error) {
            if (error instanceof LockConflictError) {
                // Somebody took the lock over WHILE this save was in flight —
                // the repository already refreshed `_loadedKnowledgeObject`'s
                // lock fields before this rejection settled, so this simply
                // reflects that: the User no longer holds it, edits made
                // since the take-over are lost (never reached the server).
                this._mode = 'view';
                container.resolve(GlobalNotificationService).error(
                    `Das Dokument wurde von einer anderen Person übernommen.`,
                    `Ihre Änderungen seit der Übernahme wurden nicht gespeichert.`,
                );
            } else {
                console.error(`Failed to save rich-text content for «${item.uid}».`, error);
                container.resolve(GlobalNotificationService).error(
                    `Dokument konnte nicht gespeichert werden.`,
                    error instanceof Error ? error.message : undefined,
                );
            }
        } finally {
            this._isSaving = false;
        }
    };

    /**
     * Discards any unsaved edit and returns to LOCKED view mode without
     * saving (Anke, 2026-09-16 — "es fehlt ein Abbrechen-Button, wenn man
     * gar nichts ändern möchte").
     *
     * Asks for confirmation FIRST when there is something to lose
     * (`_saveButtonEnabled`, mirroring the Save button's own dirty gate) —
     * declining leaves everything untouched, still in edit mode. When
     * confirmed (or when there was nothing dirty to begin with):
     *
     * 1. `editor.discardDraft()` — purges the package's own localStorage
     *    autosave draft for this object FIRST. Without this the discarded
     *    edit would silently reappear (the package's own `#applyLoad()`
     *    restores any present draft) the next time "Bearbeiten" re-enters
     *    edit mode on the same object — see that method's own doc comment
     *    for why `markSaved()` is the wrong tool for this.
     * 2. Switch to view mode. `_richTextContent` was never mutated during
     *    editing (only `_pendingDoc`, the cache `_onChanged` writes — see
     *    that field's own doc comment) and the package element's view-mode
     *    render path reads `content` directly, bypassing the live tiptap
     *    document entirely — so this alone is what makes the visible text
     *    revert, no content reassignment needed.
     * 3. Release the lock — mirrors `_onSave`'s own release-on-exit, same
     *    "must click Bearbeiten again" contract either way out of edit mode.
     */
    public _onCancel = async (): Promise<void> => {
        const item = this.item;
        if (!item || this._isSaving || this._isLoading || this._isCancelling) {
            return;
        }
        // Review fix (2026-09-16, review-02): captured BEFORE the first
        // await — mirrors `knowledge-whiteboard.component.ts`'s own
        // `_onCancel` guard (this file has the same `_loadGeneration`
        // counter, incremented on every `willUpdate` item swap). Without
        // this, a host that swaps `item` while this Cancel is still
        // awaiting the confirm dialog / lock recheck / release would have
        // this stale call write `_mode`/`discardDraft()` against the
        // FRESHLY loaded item's own editor ref and mode — discarding the
        // wrong item's draft or clobbering its just-derived mode.
        const cancelGeneration = this._loadGeneration;
        this._isCancelling = true;
        try {
            if (this._saveButtonEnabled) {
                const confirmed = await UiHelper.confirm({
                    title: get('wissen.document.cancel-confirm-title'),
                    content: html`<p>${translate('wissen.document.cancel-confirm-content')}</p>`,
                    confirmLabel: get('wissen.document.cancel-confirm-confirm'),
                    cancelLabel: get('wissen.document.cancel-confirm-cancel'),
                });
                if (!confirmed) {
                    // Review fix (2026-09-16): the confirm dialog itself was an
                    // async gap during which another user could have taken the
                    // lock over. Re-verify by re-acquiring — re-acquiring one's
                    // OWN lock is a documented no-op (LockEntityCommandHandler),
                    // so this either confirms nothing changed, or surfaces a
                    // real conflict instead of silently staying in a stale
                    // edit mode with a Save button that would only fail later.
                    const repository = container.resolve(KnowledgeObjectRepository);
                    await repository.acquireLock(item.uid).catch((error: unknown) => {
                        if (cancelGeneration !== this._loadGeneration) {
                            return;
                        }
                        if (error instanceof LockConflictError) {
                            this._mode = 'view';
                            container.resolve(GlobalNotificationService).error(
                                `Das Dokument wurde von einer anderen Person übernommen.`,
                                `Ihre Änderungen seit der Übernahme wurden nicht gespeichert.`,
                            );
                        } else {
                            console.error(`Failed to re-verify the edit lock for «${item.uid}» after a declined cancel.`, error);
                        }
                    });
                    return;
                }
            }

            // Review fix (2026-09-16, review-02): `discardDraft()` operates
            // on `_editorRef.value`, which — if the item was swapped mid-
            // flight — now points at the NEW item's editor (keyed() remounts
            // on item change), so this must be skipped, not just the plain
            // field writes, when stale. `releaseLock(item.uid)` below still
            // ALWAYS runs regardless: an abandoned edit must not leak its
            // lock just because the user navigated away before it settled.
            if (cancelGeneration === this._loadGeneration) {
                this._editorRef.value?.discardDraft();
                this._pendingDoc = null;
                this._saveButtonEnabled = false;
                this._hasUnsavedDraft = false;
                clearRteDraftMeta(item.uid);
                container.resolve(KnowledgeDraftChangeNotifier).notify(item.uid);
            }

            // ORDER (review fix, 2026-09-16): await the release BEFORE
            // flipping `_mode` — see `_onSave`'s matching comment for why.
            await container.resolve(KnowledgeObjectRepository).releaseLock(item.uid).catch((releaseError: unknown) => {
                console.error(`Failed to release the edit lock for «${item.uid}» after cancelling.`, releaseError);
            });
            if (cancelGeneration === this._loadGeneration) {
                this._mode = 'view';
            }
        } finally {
            this._isCancelling = false;
        }
    };

    /**
     * Uploads an image pasted (Strg+V) or picked inside the inner
     * `<o2d-rich-text-editor>` and registers it as a KnowledgeObject of type
     * `image` (so it shows up in the o2d file list), named `${document.title}-N`.
     * Returns the asset URL for the editor's `imageUploadHandler` seam.
     *
     * Mirrors `knowledge-whiteboard.component.ts` (the structural blueprint) so a
     * pasted image behaves identically in a document and on a whiteboard — keep
     * both in sync. The editor NEVER embeds a base64 blob when this handler is
     * bound; a rejected upload places nothing (the editor's own contract).
     */
    public _onImagePasteUpload = async (file: File): Promise<{ src: string }> => {
        const item = this.item;
        if (!item) {
            throw new Error(`Cannot upload a pasted image without a document item.`);
        }
        try {
            const asset = await container.resolve(BackendClient).uploadFile(file);
            const repository = container.resolve(KnowledgeObjectRepository);

            // The upload deduplicates by checksum, so pasting the same picture
            // again hands back the asset that already exists. Creating a second
            // image object for it would split one picture's references across
            // two identities — what is embedded and counted is the object. So
            // the object that already holds these bytes is embedded instead.
            const alreadyStored = await repository.findImageObjectUidByAsset(asset.uid);
            if (alreadyStored !== null) {
                return {src: buildAssetEmbedSrc(alreadyStored)};
            }

            const existing = await repository.findAllFiltered({type: `image`});
            const nextNumber = this._nextImageNumber(item.title, existing);

            const created = await repository.create(`${item.title}-${String(nextNumber)}`, `image`);
            // Mirrors the non-exported `ImageContentItem` of the create-dialog
            // blueprint (`knowledge-object-edit-submit.ts:37-42`) — keep both in sync.
            const content = {
                kind: `image` as const,
                assetUid: asset.uid,
                mime: asset.mime,
                sizeBytes: asset.sizeBytes,
            };
            try {
                await repository.saveContent(created.uid, [content]);
            } catch (error) {
                // Roll the orphan back, but never let a failing rollback mask
                // the original cause — the user must see why the paste failed.
                await repository.delete(created).catch(() => { /* keep original error */ });
                throw error;
            }

            return {src: buildAssetEmbedSrc(created.uid)};
        } catch (error) {
            console.error(`Failed to upload pasted image for «${item.uid}».`, error);
            container.resolve(GlobalNotificationService).error(
                `Bild konnte nicht hochgeladen werden.`,
                error instanceof Error ? error.message : undefined,
            );
            throw error;
        }
    };

    /**
     * The next `-N` suffix for a new image object under `title`, one higher than
     * the highest existing `${title}-<number>`. Mirrors the whiteboard helper.
     */
    private _nextImageNumber(title: string, existing: readonly { title: string }[]): number {
        const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, `\\$&`);
        const numberPattern = new RegExp(`^${escapedTitle}-(\\d+)$`);

        let highest = 0;
        for (const candidate of existing) {
            const match = numberPattern.exec(candidate.title ?? ``);
            if (match && Number(match[1]) > highest) {
                highest = Number(match[1]);
            }
        }

        return highest + 1;
    }

    /** @internal Public only so unit tests can walk the rendered template; Lit itself calls it. */
    public render(): HTMLTemplateResult | typeof nothing {
        const item = this.item;
        if (!item) {
            return nothing;
        }

        // `.content` + `.mode` are PROPERTY bindings; `mode` is reflected by the
        // element so the `mode="…"` attribute stays in sync. `object-id` seeds the
        // per-object localStorage draft key.
        // `keyed(item.uid, …)` forces Lit to DESTROY the previous editor and mount
        // a fresh one whenever the object changes (instead of re-binding the reused
        // element). That fires the editor's `disconnectedCallback`, which flushes a
        // pending debounced draft-save synchronously — so an unsaved draft is
        // reliably persisted on object-switch (not just on a full page close), and
        // is restored when the user returns to the object. Without this the reused
        // element never disconnects on switch, so a quick switch could drop the
        // last unsaved edits before the ~300ms autosave debounce flushed them.
        const editor = this._isContentReady
            ? keyed(item.uid, html`<o2d-rich-text-editor
                    ${ref(this._editorRef)}
                    class="knowledge-rte__editor"
                    .mode=${this._mode}
                    .content=${this._richTextContent ?? undefined}
                    .imageUploadHandler=${this._onImagePasteUpload}
                    object-id=${item.uid}
                ></o2d-rich-text-editor>`)
            : html`<div class="knowledge-rte__loading"></div>`;

        // View-mode toolbar: the "Bearbeiten" button enters edit mode. The
        // "Speichern" button (edit mode) is added in T3.3 with its save lifecycle.
        // Raw <button> is the established knowledge toolbar pattern and is NOT
        // covered by the input-components GESETZ (which scopes to <input>/<textarea>/
        // <select>). Mirrors knowledge-whiteboard's toolbar button markup.
        //
        // This wrapper-level `.knowledge-rte__toolbar` (NOT the RTE PACKAGE's
        // own internal `.o2d-rte-toolbar`, which only renders in edit mode) is
        // also where the Knowledge filter-drawer toggle button lives (Stefan,
        // 2026-08-30, moved here out of `knowledge-overview-view`'s own,
        // otherwise near-empty toolbar) — see `renderKnowledgeFilterToggleButton()`,
        // rendered FIRST in the template below (Stefan, 2026-08-30: swapped
        // ahead of Edit/Save). Always rendered regardless of view/edit mode,
        // so the toggle stays reachable either way.
        //
        // Bug fix (Anke, 2026-09-18 — "beim Umschalten in der Sidebar von
        // einem Dokument auf das andere blitzt kurz der Bearbeiten-Button
        // auf und wird danach erst zum Speichern-Button"): Edit/Save/Cancel
        // are gated on `_isContentReady` too, not just `_mode`. `willUpdate`
        // resets `_mode` to `'view'` SYNCHRONOUSLY before its first `await`
        // (Lit does not pause rendering for an async `willUpdate` beyond
        // that point) — without this gate, that intermediate reset painted
        // "Bearbeiten" for one render, then repainted as "Speichern" once
        // the load (now possibly ALSO awaiting the silent-resume lock
        // acquire, BUG2 above) resolved into edit mode: a visible flash of
        // the WRONG button. `knowledge-whiteboard.component.ts` never had
        // this problem — its Edit/Save/Cancel are injected INTO
        // `<o2d-whiteboard>`, which is itself already behind the identical
        // `_isContentReady` gate the loading placeholder below uses.
        const editLabel = translate('wissen.document.edit');
        const editButton = this._isContentReady && this._mode === 'view'
            ? html`<button type="button"
                            class="edit buttonform-marker primary"
                            data-label=${editLabel}
                            ?disabled=${this._isEditing}
                            @click=${this._onEdit}
                            title=${editLabel}
            ><div class="hover-effect"></div></button>`
            : nothing;

        // "Speichern" button (edit mode). Disabled until an edit makes the
        // document dirty, and while loading/saving (mirror whiteboard :225).
        const saveLabel = translate('wissen.document.save');
        const saveButton = this._isContentReady && this._mode === 'edit'
            ? html`<button type="button"
                            class="save buttonform-marker primary"
                            data-label=${saveLabel}
                            ?disabled=${!this._saveButtonEnabled || this._isLoading || this._isSaving}
                            @click=${this._onSave}
                            title=${saveLabel}
            ><div class="hover-effect"></div></button>`
            : nothing;

        // "Abbrechen" button (edit mode) — discards any unsaved edit and
        // returns to locked view mode, releasing the lock (Anke, 2026-09-16:
        // "es fehlt ein Abbrechen-Button, wenn man gar nichts ändern
        // möchte"). Disabled while loading/saving, same gate as Save.
        const cancelLabel = translate('wissen.document.cancel');
        const cancelButton = this._isContentReady && this._mode === 'edit'
            ? html`<button type="button"
                            class="cancel buttonform-marker secondary"
                            data-label=${cancelLabel}
                            ?disabled=${this._isLoading || this._isSaving || this._isCancelling}
                            @click=${this._onCancel}
                            title=${cancelLabel}
            ><div class="hover-effect"></div></button>`
            : nothing;

        // Unsaved-changes indicator — italic filename + trailing superscript
        // "*" (the word-processor convention: "Dokument.docx*"), instead of
        // a separate "Ungespeicherter Entwurf" text hint (Anke, 2026-09-15).
        // Mirrors knowledge-whiteboard's own `<o2d-whiteboard>.fileNameUnsaved`
        // — kept in sync manually since the two live in different render
        // paths (this filename box is wrapper-owned; the whiteboard one is
        // rendered inside the `@o2d/whiteboard` package).
        const isUnsaved = this._mode === 'edit' && this._saveButtonEnabled;

        return html`
            <div class="knowledge-rte__toolbar">
                ${renderKnowledgeFilterToggleButton()}
                ${editButton}
                ${saveButton}
                ${cancelButton}
            </div>
            <div class="knowledge-rte__filename ${isUnsaved ? 'knowledge-rte__filename--unsaved' : ''}"
            >${item.title}${isUnsaved ? html`<sup>*</sup>` : nothing}</div>
            ${editor}
        `;
    }
}
